import React, { useEffect, useState } from 'react';
import { FormControlLabel, FormHelperText, Switch } from '@material-ui/core';

import { InlineFormControl } from '../../../common/inline/InlineFormControl';
import SearchFilterButton from '../../../common/SearchFilterButton';
import SearchFilterDrawer, {
  BaseSearchFilterProps
} from '../../../common/SearchFilterDrawer';
import MultiUserSelector from '../../users/components/MultiUserSelector';
import { ProductComponent, ProductRelease } from '../Product.interface';
import ProductComponentsSelector from './ProductComponentsSelector';
import ProductEntityStatusFilter from './ProductEntityStatusFilter';
import ProductReleasesSelector from './ProductReleaseSelector';

export interface ProductFilterOptions {
  assignee: number[];
  status: string[];
  assignedForReviewOnly?: boolean;
  unassignedOnly?: boolean;
  components: ProductComponent[];
  releases: ProductRelease[];
}

function createInitialOptions(
  options?: Partial<ProductFilterOptions>
): ProductFilterOptions {
  return { assignee: [], status: [], components: [], releases: [], ...options };
}

export interface ProductBoardFilterProps
  extends BaseSearchFilterProps<ProductFilterOptions> {
  productId: number;
  options?: Partial<ProductFilterOptions>;
}

const ProductBoardFilter: React.FC<ProductBoardFilterProps> = (
  props: ProductBoardFilterProps
) => {
  const { productId, options: initialOptions, onFilter } = props;
  const [open, setOpen] = useState<boolean>(false);
  const [options, setOptions] = useState<ProductFilterOptions>(() => {
    return createInitialOptions(initialOptions);
  });

  const updateOptions = (updatedData: Partial<ProductFilterOptions>) => {
    setOptions({ ...options, ...updatedData });
  };

  useEffect(() => {
    initialOptions && updateOptions(initialOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOptions]);

  return (
    <>
      <SearchFilterButton onClick={() => setOpen(true)} />
      <SearchFilterDrawer
        open={open}
        title='Filter Products'
        onClose={() => setOpen(false)}
        onClear={() => setOptions(createInitialOptions())}
        onFilter={() => {
          setOpen(false);
          onFilter?.(options);
        }}
      >
        <InlineFormControl label='Assignee(s)'>
          <MultiUserSelector
            value={options.assignee || []}
            disabled={options.unassignedOnly || options.assignedForReviewOnly}
            textFieldProps={{ label: undefined, placeholder: 'Select User' }}
            onChange={assignee => {
              updateOptions({ assignee: assignee.map(u => u.id) });
            }}
          />
        </InlineFormControl>
        <InlineFormControl>
          <FormControlLabel
            label='Unassigned Only'
            control={
              <Switch
                color='primary'
                disabled={options.assignedForReviewOnly}
                checked={options.unassignedOnly || false}
                onChange={(_, checked: boolean) => {
                  const update: Partial<ProductFilterOptions> = {
                    unassignedOnly: !!checked
                  };
                  if (checked) {
                    update.assignee = [];
                  }
                  updateOptions(update);
                }}
              />
            }
          />
          <FormHelperText>Toggle to show unassigned items only.</FormHelperText>
        </InlineFormControl>
        <InlineFormControl>
          <FormControlLabel
            label='Assigned for Review'
            control={
              <Switch
                color='primary'
                disabled={options.unassignedOnly}
                checked={options.assignedForReviewOnly || false}
                onChange={(_, checked: boolean) => {
                  const update: Partial<ProductFilterOptions> = {
                    assignedForReviewOnly: checked || undefined
                  };
                  if (checked) {
                    update.assignee = [];
                  }
                  updateOptions(update);
                }}
              />
            }
          />
          <FormHelperText>
            Toggle to show assigned for review items only.
          </FormHelperText>
        </InlineFormControl>
        <InlineFormControl label='Status'>
          <ProductEntityStatusFilter
            value={options.status || []}
            onChange={status => {
              updateOptions({ status: status || [] });
            }}
          />
        </InlineFormControl>
        <InlineFormControl label='Component(s)'>
          <ProductComponentsSelector
            productId={productId}
            value={options.components}
            textFieldProps={{
              label: undefined,
              placeholder: 'Select Product Components'
            }}
            onChange={components => {
              updateOptions({ components });
            }}
          />
        </InlineFormControl>
        <InlineFormControl label='Release(s)'>
          <ProductReleasesSelector
            productId={productId}
            value={options.releases}
            textFieldProps={{
              label: undefined,
              placeholder: 'Select Product Releases'
            }}
            onChange={releases => {
              updateOptions({ releases });
            }}
          />
        </InlineFormControl>
      </SearchFilterDrawer>
    </>
  );
};

export default ProductBoardFilter;
